import { createSlice } from "@reduxjs/toolkit";
import { Dispatch } from "react";
import { Microservices } from "../../app/AllowedMicroservices";
import { RootState } from "../../app/store";
import apiActionCreator, { HttpMethods } from "../../services/apiActionCreator";
import { FetchStatus } from "../common/enums";

// Mirrors PUT /progress-audit/progress/:progressId/topics/:topicId/evaluations/:evaluationId/fix
// (service-learning, SuperAdmin only) - the manual correction for a topic's
// evaluation the student actually passed (confirmed against
// service-evaluation's EvaluationResult) but that never made it into
// Progress.evaluationsPassed (SYSTEM.debt.md ítem 29/§15.29). Unlike the
// lesson fix, this one can come back with an explicit error (no passed
// attempt found, or the evaluation doesn't belong to this topic), so the
// backend's message is surfaced instead of a generic one.
const DEFAULT_ERROR_MESSAGE = "No se pudo corregir la evaluación del tópico";

interface ProgressAuditFixTopicEvaluationState {
  status: FetchStatus;
}

const initialState: ProgressAuditFixTopicEvaluationState = {
  status: FetchStatus.Idle,
};

export const progressAuditFixTopicEvaluationSlice = createSlice({
  name: "progressAuditFixTopicEvaluation",
  initialState,
  reducers: {
    fixing: (state) => {
      state.status = FetchStatus.Fetching;
    },
    fixed: (state) => {
      state.status = FetchStatus.Fetched;
    },
    error: (state) => {
      state.status = FetchStatus.Error;
    },
  },
});

export const { fixing, fixed, error } = progressAuditFixTopicEvaluationSlice.actions;

export const fixProgressAuditTopicEvaluation = (
  progressId: string,
  topicId: string,
  evaluationId: string,
  onSuccess: () => void = () => {},
  onError: (message: string) => void = () => {}
) => (dispatch: Dispatch<object>) => {
  return dispatch(
    apiActionCreator({
      endpoint: `/progress-audit/progress/${progressId}/topics/${topicId}/evaluations/${evaluationId}/fix`,
      types: {
        requestType: fixing,
        successTypes: [
          {
            actionOrCreator: fixed,
            selector: () => {
              onSuccess();
            },
          },
        ],
        errorTypes: [
          {
            actionOrCreator: error,
            selector: (response: { data?: { data?: string } }) => {
              onError(response?.data?.data || DEFAULT_ERROR_MESSAGE);
            },
          },
        ],
      },
      method: HttpMethods.PUT,
      microservice: Microservices.LEARNING_SERVICE,
      authenticated: true,
    })
  );
};

export const selectIsFixingProgressAuditTopicEvaluation = (state: RootState) =>
  state.progressAuditFixTopicEvaluation.status === FetchStatus.Fetching;

export default progressAuditFixTopicEvaluationSlice.reducer;
